import { useMemo } from "react";
import type { TaskListQuery } from "@common/schemas/task.schema";
import { type Task, TaskPriority, TaskStatus } from "@common/types/task";
import { useScope } from "@state/scopeContext.lib";
import { DEMO_RUN_KINDS, type DemoRunKind } from "./busyClosesDemo.types";
import { useBusyClosesDemoTicker } from "./busyClosesDemoTicker";
import {
    buildDemoTaskDataset,
    fromDemoCloseLinksKey,
    toDemoCloseLinksKey,
} from "./buildDemoTasks";
import { fromDemoKey, isDemoTaskShown, toDemoKey } from "./demoFilters";
import {
    useDemoClock,
    useIsBusyClosesDemoOn,
    useRegisterDemoDataset,
} from "./useBusyClosesDemoGate";
import {
    useDemoOrganizationSources,
    useDemoSelfId,
    useDemoWorkspaceSources,
} from "./useDemoSources";

const ORGANIZATION_TASK_COUNT = 16;
const WORKSPACE_TASK_COUNT = 8;
const WORKSPACE_RUN_KINDS: readonly DemoRunKind[] = [
    "working",
    "waitingOnClient",
    "needsReview",
];

/** Actions tasks plus filtered fakes (16 across the organization, 8 in a workspace); `tasks` itself while off or nothing fake matches. */
export function useBusyClosesDemoTasks({
    tasks,
    scope,
    query,
}: {
    tasks: Task[] | null;
    scope: "organization" | "workspace";
    query: TaskListQuery;
}): Task[] | null {
    const isOn = useIsBusyClosesDemoOn();
    const isOrganizationScope = scope === "organization";
    const organizationSources = useDemoOrganizationSources({
        isEnabled: isOn && isOrganizationScope,
    });
    const workspaceSources = useDemoWorkspaceSources({
        isEnabled: isOn && !isOrganizationScope,
    });
    const { organizationId = null } = useScope();
    const selfId = useDemoSelfId();
    const clock = useDemoClock();
    useBusyClosesDemoTicker(isOn);

    const closeLinksKey = toDemoCloseLinksKey(tasks);
    const dataset = useMemo(() => {
        const workspaceClient = workspaceSources.client;
        const isReady = isOrganizationScope
            ? organizationSources.isReady
            : workspaceSources.isReady && workspaceClient !== null;
        if (!isOn || !isReady) {
            return null;
        }
        const workspaceClients =
            workspaceClient === null ? [] : [workspaceClient];
        return buildDemoTaskDataset({
            datasetKey: isOrganizationScope
                ? `tasks:organization:${organizationId ?? "none"}`
                : `tasks:workspace:${workspaceClient?.workspaceId ?? "none"}`,
            organizationId,
            clients: isOrganizationScope
                ? organizationSources.clients
                : workspaceClients,
            people: isOrganizationScope
                ? organizationSources.people
                : workspaceSources.people,
            selfId,
            closeLinks: fromDemoCloseLinksKey(closeLinksKey),
            count: isOrganizationScope
                ? ORGANIZATION_TASK_COUNT
                : WORKSPACE_TASK_COUNT,
            runKinds: isOrganizationScope
                ? DEMO_RUN_KINDS
                : WORKSPACE_RUN_KINDS,
            today: clock.today,
            now: clock.now,
        });
    }, [
        isOn,
        isOrganizationScope,
        organizationSources,
        workspaceSources,
        organizationId,
        selfId,
        closeLinksKey,
        clock,
    ]);
    useRegisterDemoDataset(dataset);

    const clientIdsKey = toDemoKey(query.clientIds ?? []);
    const assigneeIdsKey = toDemoKey(query.assigneeIds ?? []);
    const statusesKey = toDemoKey(query.status ?? []);
    const prioritiesKey = toDemoKey(query.priorities ?? []);
    const { isUnassigned, search } = query;
    return useMemo(() => {
        if (tasks === null || dataset === null) {
            return tasks;
        }
        const statuses = fromDemoKey(statusesKey);
        const priorities = fromDemoKey(prioritiesKey);
        const demoQuery = {
            clientIds: fromDemoKey(clientIdsKey),
            assigneeIds: fromDemoKey(assigneeIdsKey).map(Number),
            isUnassigned,
            search,
            status: Object.values(TaskStatus).filter((status) =>
                statuses.includes(status),
            ),
            priorities: Object.values(TaskPriority).filter((priority) =>
                priorities.includes(priority),
            ),
        };
        const demoTasks = dataset.tasks.filter((task) =>
            isDemoTaskShown({ task, query: demoQuery }),
        );
        return demoTasks.length === 0 ? tasks : [...tasks, ...demoTasks];
    }, [
        tasks,
        dataset,
        clientIdsKey,
        assigneeIdsKey,
        statusesKey,
        prioritiesKey,
        isUnassigned,
        search,
    ]);
}
